import type { Ride, RosterEntry } from '@thrasher/shared';
import type { Quality } from './Renderer.js';

export interface FeedItem {
  id: number;
  text: string;
  tone: 'join' | 'leave' | 'ko' | 'info';
}

export interface HudState {
  phase: 'preview' | 'joining' | 'playing' | 'error';
  error: string | null;
  connected: boolean;
  selfId: string | null;
  name: string;
  ride: Ride;
  riders: RosterEntry[];
  hp: number;
  mode: number;
  balance: number | null;
  charge: number;
  speed: number;
  trick: { id: number; text: string } | null;
  feed: FeedItem[];
  coins: number | null;
  award: { id: number; coins: number } | null;
  scoreboard: boolean;
  help: boolean;
  menu: boolean;
  muted: boolean;
  quality: Quality;
  punchReady: number;
  kickReady: number;
  koSeconds: number | null;
  protectedSeconds: number;
  hitMarker: number;
  zone: string;
}

export const INITIAL_HUD: HudState = {
  phase: 'preview',
  error: null,
  connected: false,
  selfId: null,
  name: '',
  ride: 'skateboard',
  riders: [],
  hp: 100,
  mode: 0,
  balance: null,
  charge: 0,
  speed: 0,
  trick: null,
  feed: [],
  coins: null,
  award: null,
  scoreboard: false,
  help: false,
  menu: false,
  muted: false,
  quality: 'high',
  punchReady: 1,
  kickReady: 1,
  koSeconds: null,
  protectedSeconds: 0,
  hitMarker: 0,
  zone: '',
};

/** Minimal external store for React's useSyncExternalStore. */
export class HudStore {
  private state: HudState = INITIAL_HUD;
  private readonly listeners = new Set<() => void>();
  private feedId = 0;

  public readonly subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  public readonly get = (): HudState => this.state;

  public set(partial: Partial<HudState>): void {
    let changed = false;
    for (const key of Object.keys(partial) as (keyof HudState)[]) {
      if (this.state[key] !== partial[key]) {
        changed = true;
        break;
      }
    }
    if (!changed) return;
    this.state = { ...this.state, ...partial };
    for (const listener of this.listeners) listener();
  }

  public pushFeed(text: string, tone: FeedItem['tone']): void {
    const id = (this.feedId += 1);
    this.set({ feed: [...this.state.feed.slice(-4), { id, text, tone }] });
    setTimeout(() => this.set({ feed: this.state.feed.filter((item) => item.id !== id) }), 6_000);
  }
}
